<template>
  <div class="file-upload-component">
    <div class="upload-area enhanced">
      <div class="upload-header" v-if="showHeader">
        <span class="upload-title">
          <component :is="titleIcon" class="title-icon" />
          {{ title }}
        </span>
        <span class="upload-count">{{ fileList.length }}/{{ maxCount }}</span>
      </div>
      <div class="upload-list">
        <div
            class="upload-thumb enhanced"
            v-for="(file, idx) in fileList"
            :key="file.uid || file.url || idx"
            :class="{ 'uploading': file.status === 'uploading', 'error': file.status === 'error' }"
        >
          <div class="thumb-content">
            <img
                v-if="file.url && file.status === 'done'"
                :src="file.url"
                :alt="title"
                @click="handlePreview(file.url)"
            />
            <div v-else-if="file.status === 'uploading'" class="uploading-placeholder">
              <loading-outlined class="loading-icon" />
              <span class="upload-progress">{{ file.percent || 0 }}%</span>
            </div>
            <div v-else-if="file.status === 'error'" class="error-placeholder">
              <exclamation-circle-outlined class="error-icon" />
              <span class="error-text">上传失败</span>
            </div>
          </div>
          <div class="upload-remove" @click.stop="handleRemove(file)">
            <CloseOutlined />
          </div>
        </div>
        <a-upload
            v-if="fileList.length < maxCount"
            :fileList="[]"
            :beforeUpload="handleBeforeUpload"
            :customRequest="handleCustomUpload"
            :accept="accept"
            :showUploadList="false"
            listType="picture-card"
            class="upload-btn enhanced"
            :disabled="disabled"
        >
          <div class="upload-content">
            <cloud-upload-outlined class="upload-icon" />
            <div class="upload-text">{{ uploadText }}</div>
            <div class="upload-hint" v-if="showHint">{{ uploadHint }}</div>
          </div>
        </a-upload>
        <div v-else class="upload-limit-reached">
          <div class="limit-content">
            <check-circle-outlined class="limit-icon" />
            <div class="limit-text">{{ limitText }}</div>
            <div class="limit-hint">最多 {{ maxCount }} 个文件</div>
          </div>
        </div>
      </div>
      <div class="upload-tips" v-if="showTips">
        <info-circle-outlined class="tips-icon" />
        <span>{{ tips }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadFile } from 'api/app'
import {
  CloudUploadOutlined,
  FileImageOutlined,
  FileTextOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons-vue'

export default {
  name: 'FileUpload',
  components: {
    CloudUploadOutlined,
    FileImageOutlined,
    FileTextOutlined,
    LoadingOutlined,
    ExclamationCircleOutlined,
    CloseOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined
  },
  props: {
    // 文件列表
    value: {
      type: Array,
      default: () => []
    },
    // 最大上传数量
    maxCount: {
      type: Number,
      default: 3
    },
    // 接受的文件类型
    accept: {
      type: String,
      default: '.pdf,.jpg,.png,.jpeg'
    },
    // 文件大小限制(MB)
    maxSize: {
      type: Number,
      default: 10
    },
    // 业务类型
    businessType: {
      type: String,
      default: 'vehicle-annex'
    },
    // 标题
    title: {
      type: String,
      default: '文件上传'
    },
    // 标题图标
    titleIcon: {
      type: String,
      default: 'file-image-outlined'
    },
    // 是否显示头部
    showHeader: {
      type: Boolean,
      default: true
    },
    // 上传按钮文字
    uploadText: {
      type: String,
      default: '点击上传'
    },
    // 上传提示文字
    uploadHint: {
      type: String,
      default: '或拖拽文件到此处'
    },
    // 是否显示提示
    showHint: {
      type: Boolean,
      default: true
    },
    // 达到上限文字
    limitText: {
      type: String,
      default: '已达上传上限'
    },
    // 提示信息
    tips: {
      type: String,
      default: '支持 PDF、JPG、PNG 格式，单个文件不超过 10MB'
    },
    // 是否显示提示信息
    showTips: {
      type: Boolean,
      default: true
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploadingFiles: new Set()
    }
  },
  computed: {
    fileList: {
      get() {
        return this.value || []
      },
      set(val) {
        this.$emit('input', val)
      }
    }
  },
  methods: {
    // 上传前验证
    handleBeforeUpload(file) {
      // 检查文件类型
      const acceptTypes = this.accept.split(',').map(type => type.trim())
      const fileExt = '.' + file.name.split('.').pop().toLowerCase()
      const isValidType = acceptTypes.some(type => {
        if (type.startsWith('.')) {
          return type === fileExt
        }
        return file.type.includes(type)
      })

      if (!isValidType) {
        this.$message.error({
          content: `文件格式不支持！请上传 ${this.accept} 格式的文件`,
          duration: 4
        })
        return false
      }

      // 检查文件大小
      const isValidSize = file.size / 1024 / 1024 < this.maxSize
      if (!isValidSize) {
        const fileSize = (file.size / 1024 / 1024).toFixed(2)
        this.$message.error({
          content: `文件过大！当前文件 ${fileSize}MB，请选择小于 ${this.maxSize}MB 的文件`,
          duration: 4
        })
        return false
      }

      // 显示上传开始提示
      this.$message.loading({
        content: `正在上传 ${file.name}...`,
        key: `upload-${file.uid}`,
        duration: 0
      })

      return true
    },

    // 自定义上传处理
    async handleCustomUpload(options) {
      const { file, onProgress, onSuccess, onError } = options

      // 上传开始，先加入fileList并设置uploading
      const uploadingFile = {
        uid: file.uid,
        name: file.name,
        status: 'uploading',
        url: '',
        percent: 0
      }

      const newFileList = [...this.fileList, uploadingFile]
      this.fileList = newFileList

      try {
        this.uploadingFiles.add(file.uid)
        const formData = new FormData()
        formData.append('file', file)

        const response = await uploadFile(
          this.businessType,
          formData,
          (progressEvent) => {
            if (progressEvent.lengthComputable) {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              uploadingFile.percent = percent
              onProgress({ percent })

              // 更新上传进度提示
              this.$message.loading({
                content: `正在上传 ${file.name}... ${percent}%`,
                key: `upload-${file.uid}`,
                duration: 0
              })
            }
          }
        )

        if (response && response.code == 200) {
          const ossData = response.data
          Object.assign(uploadingFile, {
            status: 'done',
            url: ossData.realPath,
            response: ossData,
            type: file.type,
            size: file.size
          })
          onSuccess(response)

          // 关闭加载提示并显示成功消息
          this.$message.destroy(`upload-${file.uid}`)
          this.$message.success({
            content: `${file.name} 上传成功！`,
            duration: 3
          })

          // 触发change事件
          this.$emit('change', this.fileList)
        } else {
          throw new Error(response?.data?.message || '上传失败')
        }
      } catch (error) {
        uploadingFile.status = 'error'
        onError(error)

        // 关闭加载提示并显示错误消息
        this.$message.destroy(`upload-${file.uid}`)
        this.$message.error({
          content: `${file.name} 上传失败：${error.message}`,
          duration: 5
        })

        // 移除失败的文件
        const index = this.fileList.findIndex(item => item.uid === file.uid)
        if (index > -1) {
          const newFileList = [...this.fileList]
          newFileList.splice(index, 1)
          this.fileList = newFileList
        }
      } finally {
        this.uploadingFiles.delete(file.uid)
      }
    },

    // 移除文件
    handleRemove(file) {
      const index = this.fileList.findIndex(item => item.uid === file.uid)
      if (index > -1) {
        const newFileList = [...this.fileList]
        newFileList.splice(index, 1)
        this.fileList = newFileList
        this.$emit('change', this.fileList)
        this.$emit('remove', file)
      }
    },

    // 预览文件
    handlePreview(url) {
      this.$emit('preview', url)
    }
  }
}
</script>

<style lang="less" scoped>
/* 文件上传样式 */
.upload-area {
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.upload-area.enhanced {
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
}

.upload-area.enhanced:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.upload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}

.upload-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;

  .title-icon {
    margin-right: 8px;
    font-size: 18px;
    color: #3b82f6;
  }
}

.upload-count {
  font-size: 14px;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 12px;
  border-radius: 12px;
  font-weight: 500;
}

.upload-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.upload-thumb {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e2e8f0;
  background: #fff;
  transition: all 0.3s ease;
}

.upload-thumb.enhanced {
  width: 90px;
  height: 90px;
  border-radius: 12px;
  border: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.upload-thumb.enhanced:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.upload-thumb.uploading {
  border-color: #3b82f6;
  background: #f0f9ff;
}

.upload-thumb.error {
  border-color: #ef4444;
  background: #fef2f2;
}

.thumb-content {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
    transition: transform 0.3s ease;

    &:hover {
      transform: scale(1.05);
    }
  }
}

.uploading-placeholder,
.error-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 12px;

  .loading-icon {
    font-size: 20px;
    color: #3b82f6;
    margin-bottom: 4px;
    animation: spin 1s linear infinite;
  }

  .error-icon {
    font-size: 20px;
    color: #ef4444;
    margin-bottom: 4px;
  }

  .upload-progress {
    color: #3b82f6;
    font-weight: 500;
  }

  .error-text {
    color: #ef4444;
    font-weight: 500;
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.upload-remove {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  background: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  font-size: 12px;
  opacity: 0;
  transition: all 0.3s ease;
  z-index: 10;
}

.upload-thumb:hover .upload-remove {
  opacity: 1;
}

.upload-remove:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.upload-btn {
  width: 90px;
  height: 90px;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-btn:hover {
  border-color: #3b82f6;
  background: #f0f9ff;
}

.upload-btn.enhanced {
  border: 2px dashed #cbd5e1;
  background: linear-gradient(135deg, #fff 0%, #f8fafc 100%);
}

.upload-btn.enhanced:hover {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  .upload-icon {
    font-size: 24px;
    color: #9ca3af;
    margin-bottom: 8px;
    transition: color 0.3s ease;
  }

  .upload-text {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
    margin-bottom: 4px;
  }

  .upload-hint {
    font-size: 12px;
    color: #9ca3af;
  }
}

.upload-btn:hover .upload-content .upload-icon {
  color: #3b82f6;
}

.upload-limit-reached {
  width: 90px;
  height: 90px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.limit-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  .limit-icon {
    font-size: 20px;
    color: #10b981;
    margin-bottom: 4px;
  }

  .limit-text {
    font-size: 12px;
    color: #374151;
    font-weight: 500;
    margin-bottom: 2px;
  }

  .limit-hint {
    font-size: 10px;
    color: #9ca3af;
  }
}

.upload-tips {
  display: flex;
  align-items: center;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  font-size: 12px;
  color: #64748b;

  .tips-icon {
    margin-right: 6px;
    color: #3b82f6;
  }
}
</style>
